import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Chip,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Close as CloseIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Place as PlaceIcon,
  VideoCall as VideoIcon,
  Link as LinkIcon,
  Notes as NotesIcon,
  Public as PublicIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerpointIcon,
  TextSnippet as TextIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';

interface DocumentInfo {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  url?: string;
}

interface MeetingData {
  id: string;
  interviewType: 'in_person' | 'video' | 'phone';
  date: string;
  time: string;
  timezone: string;
  location?: string;
  videoLink?: string;
  phoneNumber?: string;
  notes?: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
  createdAt?: string;
  updatedAt?: string;
  documents?: string[]; // Array of document IDs
}

interface ViewMeetingModalProps {
  open: boolean;
  onClose: () => void;
  meeting?: MeetingData | null;
  application?: {
    id: string;
    product?: { name?: string };
    applicant?: { id?: string; first_name?: string; last_name?: string; alias?: string };
  };
}

const ViewMeetingModal: React.FC<ViewMeetingModalProps> = ({ open, onClose, meeting, application }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Document state
  const [meetingDocuments, setMeetingDocuments] = useState<DocumentInfo[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  // Fetch meeting documents
  useEffect(() => {
    if (!meeting) {
      setMeetingDocuments([]);
      return;
    }
    const fetchMeetingDocuments = async () => {
      if (!meeting?.documents || meeting.documents.length === 0) {
        setMeetingDocuments([]);
        return;
      }

      setLoadingDocuments(true);
      setDocumentsError(null);

      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .in('id', meeting.documents);

        if (error) {
          console.error('Error fetching meeting documents:', error);
          setDocumentsError('Failed to load documents');
          return;
        }

        // Generate signed URLs for each document
        const documentsWithUrls = await Promise.all(
          (data || []).map(async (doc) => {
            try {
              const { data: urlData, error: urlError } = await supabase.storage
                .from('user-documents')
                .createSignedUrl(doc.file_path, 3600); // 1 hour expiry

              if (urlError) {
                console.error('Error creating signed URL for document:', doc.id, urlError);
                return { ...doc, url: null };
              }

              return { ...doc, url: urlData?.signedUrl || null };
            } catch (urlError) {
              console.error('Error creating signed URL for document:', doc.id, urlError);
              return { ...doc, url: null };
            }
          })
        );

        setMeetingDocuments(documentsWithUrls.filter(doc => doc.url !== null));
      } catch (error) {
        console.error('Error fetching meeting documents:', error);
        setDocumentsError('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    if (open && meeting) {
      fetchMeetingDocuments();
    }
  }, [open, meeting]);

  if (!meeting) return null;

  // Helper functions
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PdfIcon sx={{ color: '#d32f2f' }} />;
    if (fileType.includes('word') || fileType.includes('document')) return <DocumentIcon sx={{ color: '#1976d2' }} />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <ExcelIcon sx={{ color: '#388e3c' }} />;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return <PowerpointIcon sx={{ color: '#f57c00' }} />;
    if (fileType.includes('image')) return <ImageIcon sx={{ color: '#7b1fa2' }} />;
    if (fileType.includes('text')) return <TextIcon sx={{ color: '#5d4037' }} />;
    return <DocumentIcon sx={{ color: '#757575' }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <VideoIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />;
      case 'phone':
        return <PhoneIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />;
      case 'in_person':
        return <LocationIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />;
      default:
        return <EventIcon sx={{ fontSize: 24, color: theme.palette.grey[600] }} />;
    }
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return theme.palette.primary.main;
      case 'phone':
        return theme.palette.success.main;
      case 'in_person':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[600];
    }
  };

  const getMeetingTypeLabel = (type: string) => {
    switch (type) {
      case 'video':
        return 'Video Call';
      case 'phone':
        return 'Phone Call';
      case 'in_person':
        return 'In-Person Meeting';
      default:
        return 'Meeting';
    }
  };

  const formatDateTime = (date: string, time: string, timezone: string) => {
    try {
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const formattedTime = time || 'TBD';
      return `${formattedDate} at ${formattedTime} (${timezone})`;
    } catch (error) {
      return `${date} at ${time} (${timezone})`;
    }
  };

  const applicantName = application?.applicant 
    ? `${application.applicant.first_name || ''} ${application.applicant.last_name || ''}`.trim() || 
      `@${application.applicant.alias}` || 'Candidate'
    : 'Candidate';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          background: theme.palette.background.paper,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          backdropFilter: 'none',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'white',
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getMeetingTypeIcon(meeting.interviewType)}
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                Interview Meeting Details
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                {application?.product?.name || 'Job Interview'} • {applicantName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Meeting Type & Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={getMeetingTypeIcon(meeting.interviewType)}
              label={getMeetingTypeLabel(meeting.interviewType)}
              sx={{
                backgroundColor: alpha(getMeetingTypeColor(meeting.interviewType), 0.1),
                color: getMeetingTypeColor(meeting.interviewType),
                border: `1px solid ${alpha(getMeetingTypeColor(meeting.interviewType), 0.3)}`,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                height: 36,
                '& .MuiChip-icon': {
                  color: getMeetingTypeColor(meeting.interviewType),
                }
              }}
            />
            {meeting.status && (
              <Chip
                label={meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                color={meeting.status === 'completed' ? 'success' : meeting.status === 'cancelled' ? 'error' : 'primary'}
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>

          <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }} />

          {/* Date & Time */}
          <Box sx={{ 
            background: theme.palette.grey[50],
            borderRadius: 2,
            p: 2.5,
            border: `1px solid ${theme.palette.grey[200]}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight="600" color="primary.main">
                Date & Time
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ ml: 5, color: 'text.primary', fontWeight: 500 }}>
              {formatDateTime(meeting.date, meeting.time, meeting.timezone)}
            </Typography>
          </Box>

          {/* Meeting Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Video Call Details */}
            {meeting.interviewType === 'video' && meeting.videoLink && (
              <Box sx={{ 
                background: theme.palette.primary.light,
                borderRadius: 2,
                p: 2.5,
                border: `1px solid ${theme.palette.primary.main}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <VideoIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                  <Typography variant="h6" fontWeight="600" color="primary.main">
                    Video Call Link
                  </Typography>
                </Box>
                <Box sx={{ ml: 5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': { color: theme.palette.primary.dark }
                    }}
                    onClick={() => window.open(meeting.videoLink, '_blank')}
                  >
                    {meeting.videoLink}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Phone Call Details */}
            {meeting.interviewType === 'phone' && meeting.phoneNumber && (
              <Box sx={{ 
                background: theme.palette.success.light,
                borderRadius: 2,
                p: 2.5,
                border: `1px solid ${theme.palette.success.main}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    Phone Number
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 5, color: 'text.primary', fontWeight: 500 }}>
                  {meeting.phoneNumber}
                </Typography>
              </Box>
            )}

            {/* In-Person Location */}
            {meeting.interviewType === 'in_person' && meeting.location && (
              <Box sx={{ 
                background: theme.palette.warning.light,
                borderRadius: 2,
                p: 2.5,
                border: `1px solid ${theme.palette.warning.main}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <LocationIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                  <Typography variant="h6" fontWeight="600" color="warning.main">
                    Meeting Location
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 5, color: 'text.primary', fontWeight: 500 }}>
                  {meeting.location}
                </Typography>
              </Box>
            )}

            {/* Notes */}
            {meeting.notes && (
              <Box sx={{ 
                background: theme.palette.grey[100],
                borderRadius: 2,
                p: 2.5,
                border: `1px solid ${theme.palette.grey[300]}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <NotesIcon sx={{ fontSize: 24, color: theme.palette.grey[600] }} />
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    Additional Notes
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 5, color: 'text.secondary', lineHeight: 1.6 }}>
                  {meeting.notes}
                </Typography>
              </Box>
            )}

            {/* Attached Documents */}
            {meeting.documents && meeting.documents.length > 0 && (
              <Box sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                borderRadius: 2,
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AttachFileIcon sx={{ fontSize: 24, color: theme.palette.secondary.main }} />
                  <Typography variant="h6" fontWeight="600" color="secondary.main">
                    Attached Documents ({meeting.documents.length})
                  </Typography>
                </Box>
                
                {loadingDocuments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading documents...
                    </Typography>
                  </Box>
                ) : documentsError ? (
                  <Alert severity="error" sx={{ mb: 0 }}>
                    {documentsError}
                  </Alert>
                ) : meetingDocuments.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    No documents available for download.
                  </Alert>
                ) : (
                  <List sx={{ p: 0 }}>
                    {meetingDocuments.map((document, index) => (
                      <ListItem
                        key={document.id}
                        sx={{
                          background: theme.palette.background.paper,
                          borderRadius: 2,
                          mb: 1,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                          '&:hover': {
                            background: alpha(theme.palette.secondary.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <ListItemIcon>
                          {getFileIcon(document.file_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="500" color="text.primary">
                              {document.file_name}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(document.file_size)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                •
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {document.document_type}
                              </Typography>
                              {document.description && (
                                <>
                                  <Typography variant="caption" color="text.secondary">
                                    •
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {document.description}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {document.url && (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<OpenInNewIcon />}
                                  onClick={() => window.open(document.url, '_blank')}
                                  sx={{
                                    textTransform: 'none',
                                    color: theme.palette.secondary.main,
                                    '&:hover': {
                                      background: alpha(theme.palette.secondary.main, 0.1),
                                    }
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => {
                                    const link = window.document.createElement('a');
                                    link.href = document.url!;
                                    link.download = document.file_name;
                                    link.click();
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                      background: alpha(theme.palette.primary.main, 0.1),
                                    }
                                  }}
                                >
                                  Download
                                </Button>
                              </>
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>

          {/* Meeting Info Footer */}
          <Box sx={{ 
            background: theme.palette.grey[50],
            borderRadius: 2,
            p: 2,
            border: `1px solid ${theme.palette.grey[200]}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Meeting created on {meeting.createdAt ? new Date(meeting.createdAt).toLocaleDateString() : 'Unknown date'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.secondary">
                  {meeting.timezone}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        background: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '0 0 12px 12px',
      }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.grey[600]} 0%, ${theme.palette.grey[700]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.grey[700]} 0%, ${theme.palette.grey[800]} 100%)`,
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewMeetingModal;
